(ns frontend.view.modals.association.import-view
  (:require
   [clojure.string :as string]
   [common.excel-import-templates.registry :as excel-import]
   [common.localization :as loc]
   [common.schema.entity-type-names :as entity-type-names]
   [frontend.utils :as fu]
   [frontend.view.components.upload :as upload]
   [frontend.view.modals.association.events :as event]
   [frontend.view.modals.association.sub :as sub]
   [re-frame.core :as rf]
   [reagent.core :as r]
   [ui.components.alert.view :as alert]
   [ui.components.button.view :as button]
   [ui.components.grid.view :as grid]
   [ui.components.loading-spinner.view :as loading-spinner]))

(defonce modal-key :association-mass-import-modal)

(defn join-with-and
  [items]
  (cond
    (or (not items) (empty? items)) ""
    (= 1 (count items)) (first items)
    (= 2 (count items)) (str (first items) " and " (second items))
    :else (str (clojure.string/join ", " (butlast items)) ", and " (last items))))

(defn label
  ([content]
   [label {} content])
  ([props content]
   (let [mandatory? (:mandatory? props)]
     [:label
      {:class "text-xs font-semibold"}
      content
      (when mandatory?
        [:span.mandatory "*"])])))

(defn file-upload-view
  [{:keys [entity-type target-type]}]
  (let []
    [:div
     {:class "min-w-[500px]"}
     [upload/upload-zone
      {:css-class  "attachments-module-upload-zone"
       :accept     ".xlsx, .xls"
       :lang-kw    :en
       :on-change! (fn [e]
                     (let [file (aget (:files e) 0)
                           file-name (.-name file)]
                       (rf/dispatch [::event/upload-file file entity-type target-type])))}]

     [:div
      {:class "flex justify-center items-center"}
      [button/secondary-open
       {:on-click (fn []
                    (event/download-import-excel {:entity-type entity-type
                                                  :target-type target-type}))}
       [:i.fal.fa-download]
       (loc/tr :download-import-file)]]]))

(defn import-data-table
  [{:keys [data columns]}]
  (let [column-attribute->column-def (->> columns
                                          (map (fn [colum-def]
                                                 [(:attribute colum-def) colum-def]))
                                          (into {}))]
    [grid/ag-grid-enterprise
     {:theme               grid/theme-quartz
      :onGridReady         (fn [args]
                             (let [grid-api (aget args "api")
                                   size-columns-fn (aget grid-api "sizeColumnsToFit")]
                               (.call size-columns-fn grid-api)))
      :columnDefs          (->> columns
                                (map-indexed
                                 (fn [index {:keys [attribute column-type label]}]
                                   (let [last-column? (= index (dec (count columns)))]
                                     (merge
                                      {:field      (-> attribute
                                                       (str)
                                                       (subs 1))
                                       :headerName label
                                       :minWidth   120})))))
      :rowData             (fu/clj->js-keep-namespaces data)
      :domLayout           :autoHeight
      :suppressContextMenu true
      :defaultColDef       {:sortable                 false
                            :filter                   false
                            :resizable                true
                            :editable                 false
                            :suppressHeaderMenuButton true
                            :suppressMovable          true
                            :cellRenderer
                            (fn [params]
                              (let [column-id (.. params -column -colId)
                                    attribute (keyword column-id)
                                    column-def (get column-attribute->column-def attribute)
                                    column-type (:column-type column-def)

                                    row-data-js (.-data params)
                                    row-data (js->clj row-data-js :keywordize-keys true)

                                    value (case column-type
                                            :association-type (->> :association-type-labels
                                                                   (get row-data)
                                                                   (string/join "; "))
                                            :row-number (:row-number row-data)
                                            :entity-data (get-in row-data [:entity-candidate attribute])
                                            :association-comment (:association-comment row-data))

                                    import-errors (get-in row-data [:import-errors attribute])
                                    import-errors-str (when (seq import-errors)
                                                        (string/join ". " import-errors))]

                                (r/as-element
                                 [:div
                                  {:class "flex flex-col overflow-hidden truncate"}
                                  [:p
                                   {:class "truncate w-full"}
                                   value]
                                  (when import-errors-str
                                    [:p
                                     {:class "truncate text-xs text-danger-light-text w-full"}
                                     import-errors-str])])))}}]))


(defn import-errors-view
  [{:keys [entity-type target-type import-data-columns] :as view-props}]
  (let [import-data-w-errors @(rf/subscribe [::sub/import-data-with-errors])]
    [:div
     {:class "flex flex-col gap-8 flex-1"}
     [alert/danger
      {}
      [:span (loc/tr :import-file-has-errors)]
      [:div
       [:p (loc/tr :please-fix-the-errors-and-reupload-file)]]]
     [:div
      {:class "flex flex-col gap-3"}
      [import-data-table
       {:data    import-data-w-errors
        :columns (concat
                  [{:attribute   :row-number
                    :column-type :row-number
                    :label       (loc/tr :row-number)}]
                  import-data-columns)}]]
     [:div
      [:p
       {:class "font-bold"}
       (loc/tr :reupload)]

      [file-upload-view view-props]]]))

(defn confirm-associations-section
  [{:keys [state entity-type target-type import-data-columns] :as props}]
  (let [import-data @(rf/subscribe [::sub/import-data])
        import-data-w-matches (->> import-data
                                   (filterv :entity-match))
        import-data-wo-matches (->> import-data
                                    (remove :entity-match)
                                    (vec))
        entity-name-plural-lc (-> target-type
                                  (entity-type-names/get-plural :en)
                                  (string/lower-case))]
    [:div
     {:class "w-full flex flex-col gap-8"}

     (when (seq import-data-w-matches)
       [:div
        {:class "flex flex-col gap-2"}
        [:p
         (str (loc/tr :the-following) " ")
         [:b (loc/tr :existing-lc)]
         (str " " entity-name-plural-lc " ")
         (str " " (loc/tr :will-be-assigned-a-new-role))]

        [import-data-table
         {:data    import-data-w-matches
          :columns import-data-columns}]])

     (when (seq import-data-wo-matches)
       [:div
        {:class "flex flex-col gap-2"}
        [:p
         (str (loc/tr :the-following) " ")
         [:b (loc/tr :new-lc)]
         (str " " entity-name-plural-lc " ")
         (str " " (loc/tr :will-be-created-and-assigned-the-designated-role))]

        [import-data-table
         {:data    import-data-wo-matches
          :columns import-data-columns}]])]))

(defn uploading-view
  []
  [:div
   {:class "w-full h-full min-h-[220px] flex items-center justify-center"}
   [loading-spinner/circle
    {:size :xl}]])

(defn data-import-error-view
  [{:keys [data-import-error] :as view-props}]
  [:div
   [:div.mb-3
    [alert/danger
     {}
     [:span (loc/tr data-import-error)]
     [:p (loc/tr :please-check-import-file-and-try-again)]]]
   [file-upload-view view-props]])


(defn view
  [{:keys [shown? close-modal! props state class-name topmost?]}]
  (let [{:keys [entity-type target-type]} props
        import-template (excel-import/get-template {:source-entity-type entity-type
                                                    :target-entity-type target-type})
        columns (:columns import-template)]
    (r/create-class
     {:component-did-mount    (fn [] nil)
      :component-will-unmount (fn [])
      :reagent-render
      (fn [{:keys [shown? close-modal! props state class-name topmost?]}]
        (let [{:keys [entity-id entity-type target-type create-on-fly?]} props
              on-close! (fn []
                          (close-modal!))
              import-view-step @(rf/subscribe [::sub/import-flow-step])
              uploading? @(rf/subscribe [::sub/uploading?])
              create-associations-error? @(rf/subscribe [::sub/create-associations-error?])
              import-data-with-errors @(rf/subscribe [::sub/import-data-with-errors])
              parsing-error? @(rf/subscribe [::sub/parsing-error?])
              data-import-error @(rf/subscribe [::sub/data-import-error])

              view-props (merge props
                                {:import-data-columns columns
                                 :state               state})]
          [:div
           {:class "h-full min-h-[220px] gap-8 w-full flex flex-1 justify-center items-center"}
           (cond
             data-import-error
             [data-import-error-view
              (merge view-props
                     {:data-import-error data-import-error})]

             uploading?
             [uploading-view]

             (seq import-data-with-errors)
             [import-errors-view view-props]

             (= import-view-step :import-success)
             [confirm-associations-section view-props]

             :else
             [file-upload-view view-props])]))})))
